import { PipelineItem, DateRange, PipelineType } from '../types'
import { isSameDay } from 'date-fns'
// Filter data by date range
export const filterDataByDateRange = (
  data: PipelineItem[],
  dateRange: DateRange,
): PipelineItem[] => {
  return data.filter((item) => {
    // Include items that were created, drafted, or approved within the date range
    const createdInRange =
      item.createdAt >= dateRange.startDate &&
      item.createdAt <= dateRange.endDate
    const draftedInRange =
      item.firstDraftAt &&
      item.firstDraftAt >= dateRange.startDate &&
      item.firstDraftAt <= dateRange.endDate
    const approvedInRange =
      item.approvalAt &&
      item.approvalAt >= dateRange.startDate &&
      item.approvalAt <= dateRange.endDate
    return createdInRange || draftedInRange || approvedInRange
  })
}
// Filter data by pipeline type
export const filterDataByPipeline = (
  data: PipelineItem[],
  pipelineType: PipelineType,
): PipelineItem[] => {
  return data.filter((item) => item.type === pipelineType)
}
// Count items by pipeline type for a specific timestamp
export const countItemsByPipeline = (
  data: PipelineItem[],
  timestampField: keyof PipelineItem,
) => {
  const counts = {
    form: 0,
    letter: 0,
    memo: 0,
  }
  data.forEach((item) => {
    const timestamp = item[timestampField]
    if (timestamp) {
      counts[item.type]++
    }
  })
  return counts
}
// Calculate average time (in days) between two timestamps by pipeline
export const calculateAverageTimeByPipeline = (
  data: PipelineItem[],
  startField: keyof PipelineItem,
  endField: keyof PipelineItem,
) => {
  const sums = { form: 0, letter: 0, memo: 0 }
  const counts = { form: 0, letter: 0, memo: 0 }
  data.forEach((item) => {
    const startDate = item[startField] as Date
    const endDate = item[endField] as Date
    if (startDate && endDate) {
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
      const diffDays = diffTime / (1000 * 60 * 60 * 24)
      sums[item.type] += diffDays
      counts[item.type]++
    }
  })
  return {
    form: counts.form > 0 ? sums.form / counts.form : 0,
    letter: counts.letter > 0 ? sums.letter / counts.letter : 0,
    memo: counts.memo > 0 ? sums.memo / counts.memo : 0,
  }
}
// Calculate average time (in days) between two timestamps
export const calculateAverageTime = (
  data: PipelineItem[],
  startField: keyof PipelineItem,
  endField: keyof PipelineItem,
): number => {
  let sum = 0
  let count = 0
  data.forEach((item) => {
    const startDate = item[startField] as Date
    const endDate = item[endField] as Date
    if (startDate && endDate) {
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
      const diffDays = diffTime / (1000 * 60 * 60 * 24)
      sum += diffDays
      count++
    }
  })
  return count > 0 ? sum / count : 0
}
// Count items by day for a specific timestamp
export const countItemsByDay = (
  data: PipelineItem[],
  timestampField: keyof PipelineItem,
  day: Date,
): number => {
  return data.filter((item) => {
    const timestamp = item[timestampField] as Date
    return timestamp && isSameDay(timestamp, day)
  }).length
}
// Add this new function
export const getPreviousPeriodComparison = (
  data: PipelineItem[],
  dateRange: DateRange,
  field: keyof PipelineItem = 'approvalAt',
): { [key in PipelineType]: number } => {
  const currentPeriodData = filterDataByDateRange(data, dateRange)
  const previousStartDate = new Date(dateRange.startDate)
  const previousEndDate = new Date(dateRange.endDate)
  const periodLength =
    dateRange.endDate.getTime() - dateRange.startDate.getTime()
  previousStartDate.setTime(previousStartDate.getTime() - periodLength)
  previousEndDate.setTime(previousEndDate.getTime() - periodLength)
  const previousPeriodData = filterDataByDateRange(data, {
    ...dateRange,
    startDate: previousStartDate,
    endDate: previousEndDate,
  })
  const currentCounts = countItemsByPipeline(currentPeriodData, field)
  const previousCounts = countItemsByPipeline(previousPeriodData, field)
  return {
    form: calculatePercentageChange(previousCounts.form, currentCounts.form),
    letter: calculatePercentageChange(
      previousCounts.letter,
      currentCounts.letter,
    ),
    memo: calculatePercentageChange(previousCounts.memo, currentCounts.memo),
  }
}
const calculatePercentageChange = (
  previous: number,
  current: number,
): number => {
  if (previous === 0) return current > 0 ? 100 : 0
  return Math.round(((current - previous) / previous) * 100)
}
